import React, { useEffect, useMemo, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Papa from "papaparse";

/* ========== Constants ========== */

const CATEGORY_ORDER = [
  "Daal Bhaat Masu",
  "Nepali Appetizers",
  "Momo",
  "Thukpa & Noodles",
  "Appetizers",
  "Soups",
  "Mains",
  "Noodles",
  "Rice",
  "Bowls",
  "Bento Boxes [PHB Special]",
  "Thali Addons",
  "Desserts",
  "Drinks",
  "Chutney",
  "Monthly Specials",
];

const SUB_ORDER = ["Veg", "Non-Veg", "Seafood", "Mixed"];

const HIDDEN_CATEGORIES = ["staff drinks"];
const HIDDEN_ITEMS = ["open item"];

/* ========== Helpers ========== */

function formatINR(value) {
  if (value === null || value === undefined || value === "") return "";
  const num = Number(value);
  return Number.isNaN(num) ? `₹${value}` : `₹${num}`;
}

function extractVariations(row) {
  const slots = ["", ".1", ".2"];
  const variations = [];
  for (const s of slots) {
    const name = row[`Variation${s}`];
    const price = row[`Variation_Price${s}`];
    const group = row[`Variation_group_name${s}`];
    if (name && price) {
      variations.push({
        group: String(group || "Options").trim(),
        name: String(name).trim(),
        price: Number(price),
      });
    }
  }
  return variations;
}

function buildPriceLabel(basePrice, variations) {
  const base = Number(basePrice);
  const minVar =
    variations.length > 0 ? Math.min(...variations.map((v) => v.price)) : null;
  if (variations.length > 0) {
    const from =
      Number.isFinite(base) && Number.isFinite(minVar)
        ? Math.min(base, minVar)
        : Number.isFinite(minVar)
        ? minVar
        : base;
    return Number.isFinite(from) && from > 0 ? `from ${formatINR(from)}` : "";
  }
  return base > 0 ? formatINR(base) : "";
}

function deriveSection(parentCat, category) {
  const p = (parentCat || "").trim();
  if (p) return p;
  return (category || "Menu").trim();
}

function deriveSubCategory(category) {
  const c = (category || "").trim();
  const dash = c.indexOf(" - ");
  if (dash === -1) return null;
  return c.slice(dash + 3).trim() || null;
}

function getCategoryIcon(label) {
  const t = (label || "").toLowerCase();
  if (t.includes("monthly") || t.includes("re-launch") || t.includes("offer")) return "⭐";
  if (t.includes("daal") || t.includes("bhaat")) return "🍛";
  if (t.includes("thali") || t.includes("addon")) return "🍚";
  if (t.includes("momo")) return "🥟";
  if (t.includes("noodle") || t.includes("hakka") || t.includes("chow")) return "🍜";
  if (t.includes("soup") || t.includes("thukpa")) return "🥣";
  if (t.includes("appetiz") || t.includes("starter")) return "🔥";
  if (t.includes("rice")) return "🍚";
  if (t.includes("main")) return "🍛";
  if (t.includes("bowl")) return "🥘";
  if (t.includes("bento")) return "🍱";
  if (t.includes("platter")) return "🍽️";
  if (t.includes("dessert")) return "🍮";
  if (t.includes("drink") || t.includes("beverage")) return "🥤";
  if (t.includes("chutney") || t.includes("sauce")) return "🫙";
  return "🍽️";
}

function dietaryDot(attr) {
  const a = (attr || "").toLowerCase().trim();
  if (a === "veg") return "bg-green-500";
  if (a === "non-veg") return "bg-red-500";
  if (a === "egg") return "bg-amber-500";
  return null;
}

/* ========== Skeleton ========== */

function MenuSkeleton() {
  return (
    <div className="space-y-8 animate-pulse">
      <div className="flex gap-3 justify-center">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className="h-10 w-24 bg-gray-200 rounded-full" />
        ))}
      </div>
      {[1, 2].map((g) => (
        <div key={g}>
          <div className="h-8 w-40 bg-gray-200 rounded mb-4" />
          <div className="space-y-3">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="flex justify-between items-center py-3">
                <div className="space-y-2 flex-1">
                  <div className="h-4 w-48 bg-gray-200 rounded" />
                  <div className="h-3 w-64 bg-gray-100 rounded" />
                </div>
                <div className="h-4 w-12 bg-gray-100 rounded ml-4" />
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

/* ========== Main Component ========== */

export default function Menu() {
  const [items, setItems] = useState([]);
  const [activeCategory, setActiveCategory] = useState("All");
  const [dietaryFilter, setDietaryFilter] = useState("all"); // "all" | "veg" | "non-veg"
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const pillsRef = useRef(null);

  useEffect(() => {
    let cancelled = false;

    async function loadMenu() {
      try {
        setLoading(true);
        setError("");
        const res = await fetch("/menu/phb_menu.csv", { cache: "no-store" });
        if (!res.ok) throw new Error(`Failed to load menu: ${res.status}`);
        const csvText = await res.text();

        const parsed = Papa.parse(csvText, {
          header: false,
          delimiter: ",",
          skipEmptyLines: "greedy",
        });

        const nonFieldErrors = (parsed.errors || []).filter(
          (e) => !String(e.message || "").toLowerCase().includes("too few fields")
        );
        if (nonFieldErrors.length) throw new Error(nonFieldErrors[0].message || "CSV parse error");

        const rows = parsed.data || [];
        if (rows.length < 2) throw new Error("CSV appears empty");

        const headers = rows[0].map((h) => String(h || "").trim());
        const headerLen = headers.length;

        const objects = rows.slice(1).map((rowArr) => {
          const row = Array.isArray(rowArr) ? rowArr : [];
          if (row.length < headerLen) row.push(...Array(headerLen - row.length).fill(""));
          const trimmed = row.slice(0, headerLen);
          const obj = {};
          for (let i = 0; i < headerLen; i++) obj[headers[i]] = trimmed[i];
          return obj;
        });

        const normalized = objects
          .filter((r) => {
            const name = (r.Online_Name || r.Name || "").trim();
            if (!name) return false;
            if (HIDDEN_ITEMS.includes(name.toLowerCase())) return false;
            const cat = (r.Category || "").toLowerCase().trim();
            if (HIDDEN_CATEGORIES.includes(cat)) return false;
            return true;
          })
          .map((r) => {
            const variations = extractVariations(r);
            return {
              name: (r.Online_Name || r.Name || "").trim(),
              description: (r.Description || "").trim(),
              section: deriveSection(r.Parent_Category, r.Category),
              subCategory: deriveSubCategory(r.Category),
              dietary: (r.Attributes || "").trim(),
              rank: Number(r.Rank ?? 9999),
              variations,
              priceLabel: buildPriceLabel(r.Price, variations),
            };
          });

        if (!cancelled) setItems(normalized);
      } catch (e) {
        if (!cancelled) setError(e?.message || "Could not load menu");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadMenu();
    return () => { cancelled = true; };
  }, []);

  const sections = useMemo(() => {
    const present = new Set(items.map((i) => i.section));
    const ordered = CATEGORY_ORDER.filter((c) => present.has(c));
    for (const s of present) {
      if (!ordered.includes(s)) ordered.push(s);
    }
    return ordered;
  }, [items]);

  const categories = useMemo(() => ["All", ...sections], [sections]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items
      .filter((i) => (activeCategory === "All" ? true : i.section === activeCategory))
      .filter((i) => {
        if (dietaryFilter === "all") return true;
        const d = (i.dietary || "").toLowerCase();
        if (dietaryFilter === "veg") return d === "veg";
        // "non-veg" filter shows non-veg, egg, and seafood
        return d === "non-veg" || d === "egg";
      })
      .filter((i) => {
        if (!q) return true;
        return (
          i.name.toLowerCase().includes(q) ||
          i.description.toLowerCase().includes(q) ||
          (i.section || "").toLowerCase().includes(q) ||
          (i.subCategory || "").toLowerCase().includes(q)
        );
      });
  }, [items, activeCategory, dietaryFilter, query]);

  const grouped = useMemo(() => {
    const sectionMap = new Map();
    for (const item of filtered) {
      if (!sectionMap.has(item.section)) sectionMap.set(item.section, new Map());
      const subMap = sectionMap.get(item.section);
      const sub = item.subCategory || "_none";
      if (!subMap.has(sub)) subMap.set(sub, []);
      subMap.get(sub).push(item);
    }

    for (const subMap of sectionMap.values()) {
      for (const arr of subMap.values()) {
        arr.sort((a, b) => a.rank - b.rank || a.name.localeCompare(b.name));
      }
    }

    const result = [];
    const orderedSections = CATEGORY_ORDER.filter((c) => sectionMap.has(c));
    for (const s of sectionMap.keys()) {
      if (!orderedSections.includes(s)) orderedSections.push(s);
    }

    for (const sec of orderedSections) {
      const subMap = sectionMap.get(sec);
      if (!subMap) continue;
      const orderedSubs = [];
      for (const sub of SUB_ORDER) { if (subMap.has(sub)) orderedSubs.push(sub); }
      if (subMap.has("_none")) orderedSubs.push("_none");
      for (const sub of subMap.keys()) { if (!orderedSubs.includes(sub)) orderedSubs.push(sub); }

      const subGroups = orderedSubs.map((sub) => ({
        subLabel: sub === "_none" ? null : sub,
        items: subMap.get(sub) || [],
      }));
      result.push({ section: sec, subGroups });
    }
    return result;
  }, [filtered]);

  return (
    <section id="menu" className="section-padding bg-cream">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          <h2 className="section-heading">Our Menu</h2>
          <div className="accent-divider" />
          <p className="section-subheading">
            Pahadi-style comfort food, handcrafted momos, noodles &amp; Indo-Chinese favourites
          </p>
        </motion.div>

        {/* Search + Pills */}
        <motion.div
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.4, delay: 0.1 }}
          className="mb-10"
        >
          <div className="max-w-md mx-auto mb-6">
            <div className="relative">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search momos, noodles, chilli chicken..."
                className="w-full rounded-full border border-gray-200/80 bg-white/60 px-5 py-3 pl-12 text-sm outline-none placeholder:text-gray-400 focus:border-gold focus:ring-2 focus:ring-gold/20 focus:bg-white transition-all"
                aria-label="Search menu items"
              />
              <svg className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>

          {/* Veg / Non-Veg Toggle */}
          <div className="flex items-center justify-center gap-2 mb-5">
            {[
              { value: "all", label: "All", emoji: "🍽️" },
              { value: "veg", label: "Veg", emoji: "🌱" },
              { value: "non-veg", label: "Non-Veg", emoji: "🍗" },
            ].map((opt) => (
              <button
                key={opt.value}
                onClick={() => setDietaryFilter(opt.value)}
                className={`flex items-center gap-1.5 px-4 py-2 text-sm rounded-full border transition-all duration-200 ${
                  dietaryFilter === opt.value
                    ? opt.value === "veg"
                      ? "bg-green-600 text-white border-green-600 font-medium shadow-sm"
                      : opt.value === "non-veg"
                      ? "bg-red-600 text-white border-red-600 font-medium shadow-sm"
                      : "bg-black text-white border-black font-medium shadow-sm"
                    : "bg-white/70 text-gray-500 border-gray-200/60 hover:text-black hover:bg-white"
                }`}
              >
                <span>{opt.emoji}</span>
                {opt.label}
              </button>
            ))}
          </div>

          {/* Category Pills */}
          <div
            ref={pillsRef}
            className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide justify-start md:justify-center"
            role="tablist"
            aria-label="Menu categories"
          >
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setActiveCategory(c)}
                role="tab"
                aria-selected={activeCategory === c}
                className={`whitespace-nowrap rounded-full px-5 py-2.5 text-sm transition-all duration-200 shrink-0 ${
                  activeCategory === c
                    ? "bg-black text-white shadow-md font-medium"
                    : "bg-white/70 text-gray-500 hover:text-black hover:bg-white border border-gray-200/60"
                }`}
              >
                {c === "All" ? "All" : `${getCategoryIcon(c)} ${c}`}
              </button>
            ))}
          </div>
        </motion.div>

        {loading && <MenuSkeleton />}
        {error && <p className="text-redAccent text-center font-medium py-10">{error}</p>}
        {!loading && !error && grouped.length === 0 && (
          <div className="text-center py-16"><p className="text-gray-500 text-lg">No items found. Try a different search.</p></div>
        )}

        {/* Menu Sections */}
        <AnimatePresence mode="wait">
          {!loading &&
            !error &&
            grouped.map(({ section, subGroups }) => (
              <motion.div
                key={section}
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
                className="mb-14"
              >
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-2xl" aria-hidden="true">{getCategoryIcon(section)}</span>
                  <h3 className="text-2xl font-display font-semibold text-black">{section}</h3>
                  <div className="flex-1 h-px bg-gradient-to-r from-gold/40 to-transparent ml-2" />
                </div>

                {subGroups.map(({ subLabel, items: dishList }) => (
                  <div key={subLabel || "_none"} className="mb-8 last:mb-0">
                    {subLabel && subGroups.length > 1 && (
                      <div className="flex items-center gap-2 mb-3 ml-1">
                        <span
                          className={`inline-block w-2.5 h-2.5 rounded-full ${
                            subLabel === "Veg"
                              ? "bg-green-500"
                              : subLabel === "Non-Veg" || subLabel === "Seafood"
                              ? "bg-red-500"
                              : "bg-amber-500"
                          }`}
                        />
                        <span className="text-xs font-semibold uppercase tracking-wider text-gray-400">
                          {subLabel}
                        </span>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-1">
                      {dishList.map((dish, idx) => {
                        const dot = dietaryDot(dish.dietary);
                        return (
                          <div key={`${dish.name}-${idx}`} className="group py-3.5 border-b border-dashed border-gray-200/80 last:border-0">
                            <div className="flex items-baseline gap-2">
                              {dot && (
                                <span className={`inline-block w-2 h-2 rounded-full ${dot} shrink-0 translate-y-[-1px]`} title={dish.dietary} />
                              )}
                              <h4 className="text-[15px] font-medium text-black leading-snug group-hover:text-redAccent transition-colors min-w-0">
                                {dish.name}
                              </h4>
                              <div className="flex-1 border-b border-dotted border-gray-300/60 min-w-[1.5rem] translate-y-[-3px]" />
                              <span className="text-sm text-gray-400 font-light tracking-wide shrink-0 tabular-nums">
                                {dish.priceLabel}
                              </span>
                            </div>

                            {dish.description && (
                              <p className="text-gray-400 text-[13px] mt-1 leading-relaxed line-clamp-2 ml-[calc(0.5rem+8px)] max-w-sm">
                                {dish.description}
                              </p>
                            )}

                            {dish.variations.length > 0 && (
                              <div className="mt-2 ml-[calc(0.5rem+8px)] flex flex-wrap items-center gap-x-4 gap-y-1">
                                {dish.variations.map((v, i) => (
                                  <span key={i} className="text-xs text-gray-400">
                                    {v.name} <span className="text-gray-500">{formatINR(v.price)}</span>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </motion.div>
            ))}
        </AnimatePresence>
      </div>
    </section>
  );
}
